import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  InputAdornment,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { 
  LocationOn,
  MyLocation,
  Search,
  CheckCircle,
  Map as MapIcon,
  Close
} from '@mui/icons-material';
import { GOOGLE_MAPS_CONFIG } from '../config/maps';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

const LocationPage = () => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState(GOOGLE_MAPS_CONFIG.DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(GOOGLE_MAPS_CONFIG.DEFAULT_ZOOM);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  // Use global Google Maps loading
  const { isLoaded: googleMapsLoaded, isLoading: googleMapsLoading, error: googleMapsError } = useGoogleMaps();
  
  const navigate = useNavigate();
  const location = useLocation();

  // Map event handlers
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapsLoaded(true);
  }, []);


  useEffect(() => {
    getCurrentLocation();
  }, []);

  const getCurrentLocation = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by this browser');
        return;
      }

              navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setCurrentLocation({ latitude, longitude });
          
          // Get address from coordinates using backend reverse geocoding
          try {
            // Get auth token
            const customerToken = localStorage.getItem('customerToken');
            const token = localStorage.getItem('token');
            const authToken = customerToken || token;
            
            const headers = {
              'Content-Type': 'application/json',
            };
            if (authToken) {
              headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/reverse-geocode`, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify({
                lat: latitude,
                lng: longitude
              })
            });
            const data = await response.json();
            
            if (data.success && data.data) {
              const fullAddress = data.data.formatted_address;
              setAddress(fullAddress);
              setSelectedLocation({
                address: fullAddress,
                lat: latitude,
                lng: longitude
              });
              // Set map center to show the pin
              setMapCenter({ lat: latitude, lng: longitude });
            }
          } catch (error) {
            setAddress('Location detected');
          }
        },
        (error) => {
          setError('Failed to get current location. Please enter your address manually.');
        }
      );
    } catch (error) {
      setError('Failed to get current location');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchLocation = async () => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      setError('');
      
      // Get auth token
      const customerToken = localStorage.getItem('customerToken');
      const token = localStorage.getItem('token');
      const authToken = customerToken || token;
      
      const headers = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      // Use backend geocoding API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/geocode`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          address: searchQuery
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.data && data.data.length > 0) {
        const suggestions = data.data.map(result => ({
          address: result.formatted_address,
          latitude: result.geometry.location.lat,
          longitude: result.geometry.location.lng
        }));
        setSuggestions(suggestions);
      } else {
        setSuggestions([]);
        setError('No locations found for your search');
      }
    } catch (error) {
      setError('Failed to search for location');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLocation = (suggestion) => {
    setSelectedLocation(suggestion);
    setAddress(suggestion.address);
    setSuggestions([]);
    setSearchQuery(suggestion.address);
    // Set map center to show the pin
    setMapCenter({ lat: suggestion.latitude, lng: suggestion.longitude });
  };

  const handleConfirmLocation = async () => {
    if (!selectedLocation) {
      setError('Please select a location');
      return;
    }

    try {
      // Save location to localStorage
      localStorage.setItem('userLocation', JSON.stringify(selectedLocation));
      
      // Check if we need to return to a specific page
      const returnTo = location.state?.returnTo;
      if (returnTo) {
        // Navigate back to the return page with the selected address
        navigate(returnTo, {
          state: {
            selectedAddress: selectedLocation.address
          }
        });
      } else {
        // Navigate to home page
        navigate('/home');
      }
    } catch (error) {
      setError('Failed to save location');
    }
  };

  const handleSkipLocation = () => {
    navigate('/home');
  };

  return (
    <Container component="main" maxWidth="md" className="auth-container">
      <Paper elevation={3} className="auth-paper">
        <Box className="auth-header">
          <Typography variant="h4" component="h1" className="auth-title">
            Set Your Location
          </Typography>
          <Typography variant="body1" className="auth-subtitle">
            Help us find services near you by setting your location
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" className="auth-alert">
            {error}
          </Alert>
        )}

        <Box className="auth-form">
          <Button
            fullWidth
            variant="outlined"
            onClick={getCurrentLocation}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <MyLocation />}
            className="auth-button"
            sx={{ marginBottom: 3 }}
          >
            {loading ? 'Getting Location...' : 'Use Current Location'}
          </Button>

          <Divider sx={{ marginY: 2 }}>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Divider>

          <TextField
            fullWidth
            label="Search for a location"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && searchQuery.trim()) {
                handleSearchLocation();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {searchQuery && (
                      <IconButton
                        onClick={() => {
                          setSearchQuery('');
                          setSuggestions([]);
                          setError('');
                        }}
                        size="small"
                        title="Clear search"
                        sx={{ mr: 0.5 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                    <Button
                      onClick={handleSearchLocation}
                      disabled={!searchQuery.trim() || loading}
                    >
                      Search
                    </Button>
                  </Box>
                </InputAdornment>
              ),
            }}
            className="auth-input"
            sx={{ marginBottom: 2 }}
          />

          {suggestions.length > 0 && (
            <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1, marginBottom: 2 }}>
              {suggestions.map((suggestion, index) => (
                <React.Fragment key={index}>
                  <ListItem 
                    button 
                    onClick={() => handleSelectLocation(suggestion)}
                    selected={selectedLocation?.address === suggestion.address}
                  >
                    <ListItemIcon>
                      <LocationOn color="action" />
                    </ListItemIcon>
                    <ListItemText primary={suggestion.address} />
                    {selectedLocation?.address === suggestion.address && (
                      <CheckCircle color="primary" />
                    )}
                  </ListItem>
                  {index < suggestions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {selectedLocation && (
            <Alert severity="success" sx={{ marginBottom: 2 }}>
              Selected: {selectedLocation.address}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setMapDialogOpen(true)}
              startIcon={<MapIcon />}
              className="auth-button"
              sx={{ flex: 1, minWidth: 120 }}
            >
              Open Map
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleConfirmLocation}
              disabled={!selectedLocation}
              className="auth-button"
              sx={{ flex: 1, minWidth: 120 }}
            >
              Confirm Location
            </Button>
            <Button
              variant="outlined"
              onClick={handleSkipLocation}
              className="auth-button"
              sx={{ flex: 1, minWidth: 120 }}
            >
              Skip for Now
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Google Maps Dialog */}
      <Dialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon />
            <Typography variant="h6">Select Location on Map</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <Box sx={{ width: '100%', height: '60vh', minHeight: 400, position: 'relative' }}>
            {!googleMapsLoaded ? (
              googleMapsLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2
                }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary">Loading Google Maps...</Typography>
                </Box>
              ) : googleMapsError ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2,
                  p: 3
                }}>
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>Failed to load Google Maps: {googleMapsError.message}</Typography>
                  <Button onClick={getCurrentLocation}>Use Current Location</Button>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2
                }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary">Initializing map...</Typography>
                </Box>
              )
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : mapCenter}
                zoom={GOOGLE_MAPS_CONFIG.DEFAULT_ZOOM}
                onLoad={onMapLoad}
                options={GOOGLE_MAPS_CONFIG.MAP_OPTIONS}
              >
                {/* Show marker when we have a selected location */}
                {selectedLocation && (
                  <Marker
                    position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                    icon={window.google && window.google.maps && window.google.maps.Size ? {
                      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 2C10.5 2 6 6.5 6 12c0 7 10 20 10 20s10-13 10-20c0-5.5-4.5-10-10-10zm0 13c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z" fill="#1976d2" stroke="white" stroke-width="2"/>
                        </svg>
                      `),
                      scaledSize: new window.google.maps.Size(32, 32),
                      anchor: new window.google.maps.Point(16, 16)
                    } : undefined}
                  />
                )}
              </GoogleMap>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              // Handle map location selection
              if (selectedLocation) {
                handleConfirmLocation();
                setMapDialogOpen(false);
              }
            }}
            disabled={!selectedLocation}
          >
            Select This Location
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LocationPage; 